import { fmtDate } from "@/lib/format";

const sum = (l, k = "amount") => l.reduce((a, r) => a + Number(r[k] || 0), 0);

export function regionGroups(regions, churches) {
  const ids = new Set(regions.map((r) => r.id));
  const groups = regions.map((r) => ({ key: r.id, name: r.name, churches: churches.filter((c) => c.region_id === r.id) }));
  const rest = churches.filter((c) => !ids.has(c.region_id));
  return rest.length ? [...groups, { key: "unassigned", name: "Unassigned", churches: rest }] : groups;
}

export function buildConference({ regions, churches, collection, data }) {
  const def = Number(collection?.goal || 0);
  const goalOf = (c) => Number(collection?.church_goals?.[c.id] ?? def);
  const groups = regionGroups(regions, churches).map((g) => {
    const rows = g.churches.map((c) => {
      const pays = data.donations.filter((d) => d.church_id === c.id);
      const goal = goalOf(c);
      const paid = sum(pays);
      return { church: c, goal, pays, paid, remaining: Math.max(goal - paid, 0), custom: collection?.church_goals?.[c.id] !== undefined };
    });
    return { ...g, rows, goal: sum(rows, "goal"), paid: sum(rows, "paid"), remaining: sum(rows, "remaining") };
  }).filter((g) => g.rows.length);
  const cols = Math.max(1, ...groups.flatMap((g) => g.rows.map((r) => r.pays.length)));
  const regMap = {};
  data.registrations.forEach((r) => {
    const k = `${r.event_name}|${r.fee_category || "Adult"}`;
    regMap[k] = regMap[k] || { event: r.event_name, cat: r.fee_category || "Adult", n: 0, amount: 0 };
    regMap[k].n += 1;
    regMap[k].amount += Number(r.fee_amount || 0);
  });
  const regRows = Object.values(regMap);
  const t = { goal: sum(groups, "goal"), paid: sum(groups, "paid"), remaining: sum(groups, "remaining"), reg: sum(regRows), exp: sum(data.expenses) };
  t.income = t.paid + t.reg;
  t.balance = t.income - t.exp;
  return { def, groups, cols, regRows, t, expenses: data.expenses };
}

const blank = (n) => Array(n).fill("");

export function conferenceSections(m, detail) {
  const income = { title: "Income", head: ["", "Amount"], body: [["Church payments", m.t.paid], ["Registration income", m.t.reg]], foot: ["TOTAL INCOME", m.t.income], moneyCols: [1] };
  const balance = { title: "Balance", head: ["", "Amount"], body: [["Total income", m.t.income], ["Total expenses", m.t.exp]], foot: ["BALANCE", m.t.balance], moneyCols: [1] };
  if (!detail) {
    return [
      { title: "Church payments by region", head: ["Region", "Churches", "Goal", "Total paid", "Remaining"], body: m.groups.map((g) => [g.name, g.rows.length, g.goal, g.paid, g.remaining]), foot: ["TOTAL", m.groups.reduce((a, g) => a + g.rows.length, 0), m.t.goal, m.t.paid, m.t.remaining], moneyCols: [2, 3, 4] },
      income,
      { title: "Expenses", head: ["", "Amount"], body: [["Total expenses", m.t.exp]], moneyCols: [1] },
      balance,
    ];
  }
  const payHead = Array.from({ length: m.cols }, (_, i) => `Payment ${i + 1}`);
  const body = [];
  const strong = [];
  m.groups.forEach((g) => {
    strong.push(body.length); body.push([g.name, ...blank(m.cols + 3)]);
    g.rows.forEach((r) => body.push([r.church.name, r.goal, ...Array.from({ length: m.cols }, (_, i) => (r.pays[i] ? `${Number(r.pays[i].amount).toFixed(2)} (${fmtDate(r.pays[i].date)})` : "")), r.paid, r.remaining]));
    strong.push(body.length); body.push([`${g.name} sub-total`, g.goal, ...blank(m.cols), g.paid, g.remaining]);
  });
  return [
    { title: "Church payments", head: ["Church", "Goal", ...payHead, "Total paid", "Remaining"], body, strong, foot: ["TOTAL", m.t.goal, ...blank(m.cols), m.t.paid, m.t.remaining], moneyCols: [1, m.cols + 2, m.cols + 3] },
    { title: "Registration income", head: ["Event", "Category", "Paid registrations", "Amount"], body: m.regRows.map((r) => [r.event, r.cat, r.n, r.amount]), foot: ["TOTAL", "", m.regRows.reduce((a, r) => a + r.n, 0), m.t.reg], moneyCols: [3] },
    income,
    { title: "Expenses", head: ["Date", "Paid to", "Description", "Amount"], body: m.expenses.map((e) => [fmtDate(e.date), e.paid_to || "", e.description || "", Number(e.amount || 0)]), foot: ["TOTAL EXPENSES", "", "", m.t.exp], moneyCols: [3] },
    balance,
  ];
}
